import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpCircle,
  CheckCircle2,
  Circle,
  Loader2,
  Lock,
  AlertTriangle,
  RefreshCw,
  CreditCard,
  Info,
  ChevronRight,
  GraduationCap,
  Hourglass,
  Award,
  BadgeCheck,
  ArrowLeft,
  Upload,
} from "lucide-react";
import {
  useGetUpgradeEligibilityQuery,
  useInitiateUpgradePaymentMutation,
  useGetUpgradeCurrentQuery,
  useUpgradeResubmitMutation,
} from "../api/upgradeApi";
import { useSubmitManualCpdMutation } from "../../cpd/api/cpdApi";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
type RouteOption = {
  route: string;
  fee: number | null;
  fee_kobo: number | null;
  cpd_required: number | null;
  cpd_met: boolean;
  exam_required?: string | null;
  exam_result_met?: boolean;
};
type Blocker = {
  code: string;
  title: string;
  message: string;
  // Whether this bar can be answered with a DECLARATION rather than cleared.
  // Set by the API, not inferred here: fellowship candidates may confirm they
  // hold the CPD where the ledger cannot show it, and no other grade may.
  attestable?: boolean;
  action?: { type: string; endpoint?: string; next_step?: number };
  meta?: Record<string, any>;
};

/* ------------------------------------------------------------------ */
/* Route presentation                                                  */
/* ------------------------------------------------------------------ */
const ROUTE_META: Record<
  string,
  { label: string; blurb: string; icon: any; accent: string }
> = {
  exams: {
    label: "Exams Route",
    blurb:
      "Upgrade by sitting and passing the Institute's professional examinations.",
    icon: GraduationCap,
    accent: "indigo",
  },
  matured: {
    label: "Matured Route",
    blurb:
      "Upgrade as a mature candidate, based on your years of professional experience — without sitting the examinations.",
    icon: Hourglass,
    accent: "amber",
  },
};

const ACCENT: Record<string, { ring: string; bg: string; text: string }> = {
  indigo: { ring: "border-indigo-500 ring-indigo-100 bg-indigo-50", bg: "bg-indigo-100", text: "text-indigo-600" },
  amber: { ring: "border-amber-500 ring-amber-100 bg-amber-50", bg: "bg-amber-100", text: "text-amber-600" },
};

const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The reason an account has no upgrade target. Prefers the backend's specific
 * message; falls back to a grade-aware sentence so it's never just generic.
 */
export function noUpgradeReason(elig: any): string {
  const backend = elig?.payment_blockers?.[0]?.message;
  if (backend) return backend;

  const name = elig?.current_grade?.name as string | undefined;
  if (!name) {
    return "Your account does not have a membership grade assigned, so there is no upgrade to apply for. This is normal for staff/admin accounts.";
  }
  const n = name.toLowerCase();
  if (n === "fellow")
    return "You are already a Fellow — the highest membership grade. There is no further upgrade.";
  if (n === "technician")
    return "Technician membership is not part of the upgrade ladder (Student → Probationer → Member → Fellow).";
  if (n === "firm")
    return "Firm (corporate) accounts are not eligible for a membership grade upgrade.";
  return `Your current grade (${name}) is not eligible for a membership grade upgrade.`;
}

/** Build and auto-submit the OnePay POST form (same as the application fee). */
function redirectToOnePay(res: any) {
  const form = document.createElement("form");
  form.method = "POST";
  form.id = "OnePay_form";
  form.name = "OnePay_form";
  form.action = res.payment_url || "https://vuvaa.com/ONEPAY/mod/live/main";
  const fields: Record<string, any> = {
    merchant_reg_id: res.merchant_reg_id,
    amt_paid: res.amt_paid,
    merch_trans_id: res.merch_trans_id,
    client_phone: res.client_phone,
    product_desc: res.product_desc,
    client_email: res.client_email,
    client_name: res.client_name,
  };
  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value ?? "");
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

/* ------------------------------------------------------------------ */
/* "Why you can't proceed" screen                                      */
/* ------------------------------------------------------------------ */
function NotEligibleScreen({
  title,
  message,
  tone = "amber",
  badge,
  onBack,
}: {
  title: string;
  message: string;
  tone?: "amber" | "indigo";
  badge?: string;
  onBack?: () => void;
}) {
  const c =
    tone === "indigo"
      ? { border: "border-indigo-200", bg: "from-indigo-50 to-blue-50", icon: "bg-indigo-100 text-indigo-600", title: "text-indigo-900", text: "text-indigo-800" }
      : { border: "border-amber-200", bg: "from-amber-50 to-orange-50", icon: "bg-amber-100 text-amber-600", title: "text-amber-900", text: "text-amber-800" };
  const Icon = tone === "indigo" ? Info : Lock;
  return (
    <div className={`rounded-2xl border ${c.border} bg-gradient-to-br ${c.bg} p-8 text-center`}>
      <div className={`w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-4 shadow-inner ${c.icon}`}>
        <Icon className="w-8 h-8" />
      </div>
      <h2 className={`text-xl font-bold ${c.title}`}>{title}</h2>
      <p className={`text-sm mt-2 max-w-md mx-auto ${c.text}`}>{message}</p>
      {badge && (
        <span className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-white border border-amber-200 text-amber-700 text-xs font-semibold">
          <Award className="w-4 h-4" /> {badge}
        </span>
      )}
      <div className="mt-6 flex items-center justify-center gap-2">
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        <Link
          to="/upgrade-applications"
          className="px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
        >
          My applications
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Blocker card                                                        */
/* ------------------------------------------------------------------ */
function BlockerCard({
  blocker,
  onClaimed,
  attested,
  onAttest,
}: {
  blocker: Blocker;
  onClaimed?: () => void;
  attested?: boolean;
  onAttest?: (on: boolean) => void;
}) {
  const isCpd = blocker.code === "CPD_BELOW_MINIMUM";
  return (
    <div className="flex gap-3 p-4 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="w-9 h-9 rounded-lg bg-amber-100 grid place-items-center shrink-0">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
      </div>
      <div className="space-y-2 text-sm flex-1 min-w-0">
        <p className="font-semibold text-amber-900">{blocker.title}</p>
        <p className="text-amber-800">{blocker.message}</p>
        {isCpd && blocker.meta && (
          <div className="flex flex-wrap gap-2 pt-1">
            <span className="px-2 py-0.5 rounded-md bg-white border border-amber-200 text-amber-700 text-xs font-semibold">
              You have: {blocker.meta.cpd_effective ?? blocker.meta.cpd_current} CPD
            </span>
            <span className="px-2 py-0.5 rounded-md bg-white border border-amber-200 text-amber-700 text-xs font-semibold">
              Required: {blocker.meta.cpd_required} CPD
            </span>
            {Number(blocker.meta.cpd_shortfall) > 0 && (
              <span className="px-2 py-0.5 rounded-md bg-white border border-amber-200 text-amber-700 text-xs font-semibold">
                Short by: {blocker.meta.cpd_shortfall} CPD
              </span>
            )}
          </div>
        )}
        {isCpd && <CpdUploadForm onClaimed={onClaimed} />}

        {/* The declaration. Offered ALONGSIDE the upload, never instead of it —
            a candidate who can produce the certificates should, and this is for
            the CPD that predates the ledger and cannot be produced. */}
        {blocker.attestable && onAttest && (
          <label className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-white/70 p-3 text-sm text-amber-900 cursor-pointer">
            <input
              type="checkbox"
              checked={!!attested}
              onChange={(ev) => onAttest(ev.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              I confirm that I hold the CPD units required for this upgrade. I understand this
              declaration is recorded against my application and seen by the Membership Committee
              when they consider it.
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

/* Inline CPD certificate upload — self-declared points; counts immediately. */
function CpdUploadForm({ onClaimed }: { onClaimed?: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [date, setDate] = useState("");
  const [points, setPoints] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [submit, { isLoading }] = useSubmitManualCpdMutation();

  const onSubmit = async () => {
    setMsg(null);
    const pts = Number(points);
    if (!title.trim() || !date || !file || isNaN(pts) || pts <= 0) {
      setMsg({ type: "err", text: "Enter a title, date, CPD points (> 0) and attach a certificate." });
      return;
    }
    const fd = new FormData();
    fd.append("title", title.trim());
    if (organizer.trim()) fd.append("organizer", organizer.trim());
    fd.append("activity_date", date);
    fd.append("cpd_points_claimed", String(pts));
    fd.append("certificate", file);
    try {
      const res: any = await submit(fd).unwrap();
      setMsg({ type: "ok", text: res?.message || "CPD certificate submitted." });
      setTitle(""); setOrganizer(""); setDate(""); setPoints(""); setFile(null);
      onClaimed?.(); // refetch eligibility → the claimed points now count
    } catch (err: any) {
      setMsg({ type: "err", text: err?.data?.message || "Upload failed. Please try again." });
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
      >
        <Upload className="w-3.5 h-3.5" /> Upload CPD certificate to top up
      </button>
    );
  }

  return (
    <div className="mt-2 p-3 rounded-lg bg-white border border-amber-200 space-y-2">
      <p className="text-[11px] text-gray-500">
        Upload a certificate for CPD you earned outside the events list and enter the CPD units it carries.
        It counts toward your requirement now and is confirmed on review.
      </p>
      <input
        type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="Activity / course title *"
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <input
        type="text" value={organizer} onChange={(e) => setOrganizer(e.target.value)}
        placeholder="Organizer (optional)"
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <input
          type="date" value={date} onChange={(e) => setDate(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="number" min={0.5} step={0.5} value={points} onChange={(e) => setPoints(e.target.value)}
          placeholder="CPD units *"
          className="w-28 border rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <input
        type="file" accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="w-full text-xs"
      />
      {msg && (
        <p className={`text-xs font-medium ${msg.type === "ok" ? "text-emerald-700" : "text-rose-600"}`}>
          {msg.text}
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={onSubmit} disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {isLoading ? "Uploading…" : "Submit certificate"}
        </button>
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700">
          Cancel
        </button>
      </div>
      <p className="text-[10px] text-gray-400">
        Or manage all your CPD claims on the <Link to="/my-cpd" className="underline">CPD page <ChevronRight className="w-3 h-3 inline" /></Link>
      </p>
    </div>
  );
}

/* ================================================================== */
/* PAYMENT STEP                                                         */
/* ================================================================== */
export default function UpgradePaymentStep({ onBack }: { onBack?: () => void }) {
  const { data: e, isLoading, isFetching, isError, refetch } =
    useGetUpgradeEligibilityQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const navigate = useNavigate();
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
  const [serverBlockers, setServerBlockers] = useState<Blocker[] | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [initiate, { isLoading: isPaying }] = useInitiateUpgradePaymentMutation();
  const [cpdAttested, setCpdAttested] = useState(false);

  // A reopened-after-rejection application that was already PAID resubmits
  // WITHOUT paying again. Gate on the payment actually being settled — a merely
  // started-then-cancelled attempt leaves a payment_id but no paid payment, and
  // must still go through payment.
  const { data: current } = useGetUpgradeCurrentQuery();
  const alreadyPaid =
    String((current as any)?.payment?.status ?? "").toLowerCase() === "paid";
  const [resubmit, { isLoading: isResubmitting }] = useUpgradeResubmitMutation();

  const handleResubmit = async () => {
    setPayError(null);
    try {
      await resubmit({ id: current.id }).unwrap();
      navigate(`/upgrade-applications/grade/${current.id}`);
    } catch (err: any) {
      setPayError(err?.data?.message || err?.message || "Failed to resubmit.");
    }
  };

  const routeOption = useMemo<RouteOption | null>(() => {
    if (!e?.requires_route || !selectedRoute || !e?.routes) return null;
    return e.routes.find((r: RouteOption) => r.route === selectedRoute) ?? null;
  }, [e, selectedRoute]);

  const localBlockers = useMemo<Blocker[]>(() => {
    if (!e) return [];
    const list: Blocker[] = [...(e.payment_blockers ?? [])];

    // The API already reports a CPD shortfall for the selected route, and its
    // copy is the one that carries `attestable`. Adding a second, locally
    // built one would make every blocker "not declarable" — the declaration
    // would be ticked and the applicant still stuck.
    const cpdAlreadyReported = list.some((b) => b.code === "CPD_BELOW_MINIMUM");

    if (e.requires_route && routeOption && !routeOption.cpd_met && !cpdAlreadyReported) {
      // Effective CPD = awarded + pending claims (what the gate now uses).
      const effective = e.cpd?.effective ?? e.cpd?.total ?? 0;
      const shortfall = Math.max(0, (routeOption.cpd_required ?? 0) - effective);
      list.push({
        code: "CPD_BELOW_MINIMUM",
        title: "Not enough CPD units",
        // Whether a declaration answers this is the API's call, never the
        // screen's — it is the thing that knows this is a fellowship target.
        attestable: !!e.cpd?.attestable,
        message: `You need at least ${routeOption.cpd_required} CPD units to upgrade via the ${cap(
          routeOption.route
        )} route. You currently have ${effective} (${shortfall} short). Upload certificates for any CPD you have not yet reported to make up the difference.`,
        action: { type: "cpd_claim" },
        meta: {
          cpd_current: e.cpd.total,
          cpd_effective: effective,
          cpd_required: routeOption.cpd_required,
          cpd_shortfall: shortfall,
          pending_claims_points: e.cpd.pending_claims_points,
        },
      });
    }
    if (e.requires_route && routeOption && routeOption.exam_result_met === false) {
      list.push({
        code: "EXAM_RESULT_REQUIRED",
        title: "Exam result required",
        message: `To upgrade via the ${cap(routeOption.route)} route, you must first have a recommended result in the ${
          routeOption.exam_required ?? "required interview"
        }.`,
      });
    }
    return list;
  }, [e, routeOption]);

  const blockers = serverBlockers ?? localBlockers;

  // A fellowship CPD shortfall is declarable rather than disqualifying. The
  // button stays shut until the declaration is actually ticked, and only when
  // EVERY outstanding bar is one a declaration answers — anything else still
  // has to be cleared.
  const attestableOnly = blockers.length > 0 && blockers.every((b) => b.attestable);
  const cpdDeclared = attestableOnly && cpdAttested;
  const blockersCleared = blockers.length === 0 || cpdDeclared;
  const feeToPay = e?.requires_route ? routeOption?.fee ?? null : e?.fee ?? null;

  const canPay =
    !!e &&
    !e.active_upgrade &&
    e.form_complete &&
    // The route's own CPD test has to admit the declaration too. It is a
    // second reading of the same bar, and left alone it kept the button shut
    // on exactly the candidates the declaration exists for.
    //
    // Only the CPD bar bends. `cpdDeclared` requires EVERY outstanding blocker
    // to be declarable, so an unmet exam result — which is never declarable —
    // still stops it here and in the list below.
    (!e.requires_route ||
      (!!routeOption &&
        (routeOption.cpd_met || cpdDeclared) &&
        routeOption.exam_result_met !== false)) &&
    blockersCleared &&
    feeToPay != null;

  // Plain-language reason the Pay button is disabled (shown under it).
  let disabledReason: string | null = null;
  if (e && !canPay) {
    if (e.active_upgrade) disabledReason = "You already have an upgrade application being processed.";
    else if (!e.form_complete) disabledReason = "Complete and acknowledge your application (step 8) before paying.";
    else if (e.requires_route && !routeOption) disabledReason = "Select an upgrade route above to continue.";
    // Asked before the CPD minimum: for a fellowship candidate the declaration
    // IS the answer to that minimum, and naming the shortfall instead would
    // send them off to chase certificates they have already been told they do
    // not need to file.
    else if (attestableOnly && !cpdAttested) disabledReason = "Tick the declaration above to confirm that you hold the required CPD units.";
    else if (e.requires_route && routeOption && !routeOption.cpd_met) disabledReason = "You don't meet the CPD minimum for the selected route.";
    else if (e.requires_route && routeOption && routeOption.exam_result_met === false) disabledReason = `You need a recommended ${routeOption.exam_required ?? "interview"} result for this route.`;
    else if (feeToPay == null) disabledReason = "The upgrade fee is not configured — please contact the secretariat.";
  }

  const handlePay = async () => {
    setPayError(null);
    setServerBlockers(null);
    try {
      const body: Record<string, any> = e?.requires_route ? { route: selectedRoute! } : {};
      // Sent with the payment, so the one call files the declaration and
      // charges — the same shape the exam flow uses.
      if (cpdAttested) body.cpd_attested = true;
      const res = await initiate(body).unwrap();
      if (!res?.merchant_reg_id || !res?.merch_trans_id) {
        setPayError("The payment gateway returned an invalid response.");
        return;
      }
      redirectToOnePay(res);
    } catch (err: any) {
      if (err?.status === 422 && Array.isArray(err?.data?.blockers)) {
        setServerBlockers(err.data.blockers);
        refetch();
      } else {
        setPayError(err?.data?.message || err?.message || "Failed to start payment.");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="py-16 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Checking your upgrade eligibility…</p>
      </div>
    );
  }

  if (isError || !e) {
    return (
      <div className="py-12 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not check your upgrade eligibility.</p>
        <button onClick={() => refetch()} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
          Retry
        </button>
      </div>
    );
  }

  // ── Not eligible: no upgrade target for this account ────────────────────
  if (!e.target_grade) {
    return (
      <NotEligibleScreen
        title="No upgrade available"
        message={noUpgradeReason(e)}
        badge={e.current_grade ? `Current grade: ${e.current_grade.name}` : undefined}
        onBack={onBack}
      />
    );
  }

  // ── Not eligible: an upgrade is already in flight ───────────────────────
  if (e.active_upgrade) {
    return (
      <NotEligibleScreen
        tone="indigo"
        title="Upgrade already in progress"
        message="You already have an upgrade application being processed. You can track it from your applications list."
        onBack={onBack}
      />
    );
  }

  const cpdMet = e.cpd_required == null ? true : (e.cpd?.met ?? false);
  const cpdTarget = e.requires_route
    ? Math.min(
        ...(e.routes ?? [])
          .map((r: RouteOption) => r.cpd_required ?? Infinity)
          .filter((n: number) => Number.isFinite(n)),
        Infinity
      )
    : e.cpd_required ?? 0;
  const cpdPct =
    cpdTarget && Number.isFinite(cpdTarget) && cpdTarget > 0
      ? Math.min(100, Math.round((e.cpd.total / cpdTarget) * 100))
      : 100;

  return (
    <div className="space-y-5">
      {/* Heading */}
      <div className="flex items-center gap-2 text-gray-900">
        <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
        <h2 className="text-lg font-bold">
          Pay to submit: {e.current_grade?.name} → {e.target_grade?.name}
        </h2>
      </div>

      {/* CPD progress */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between text-xs font-medium mb-2">
          <span className="text-gray-600">
            Your CPD: <span className="font-bold text-gray-900">{e.cpd.total}</span>
            {Number.isFinite(cpdTarget) && cpdTarget > 0 && (
              <span className="text-gray-400"> / {cpdTarget} min</span>
            )}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
              cpdMet ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {cpdMet ? <BadgeCheck className="w-3.5 h-3.5" /> : <Hourglass className="w-3.5 h-3.5" />}
            {cpdMet ? "Minimum met" : "Below minimum"}
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full rounded-full ${cpdMet ? "bg-green-500" : "bg-amber-500"}`}
            style={{ width: `${cpdPct}%` }}
          />
        </div>
        {e.cpd.pending_claims_points > 0 && (
          <p className="text-[11px] text-gray-500 mt-2">
            + {e.cpd.pending_claims_points} CPD in claims awaiting approval (counts once approved).
          </p>
        )}
      </div>

      {/* Route selection */}
      {e.requires_route && (
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-1">Choose your upgrade route</p>
          <p className="text-xs text-gray-500 mb-3">
            Probationers upgrade to Member via one of two routes.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(e.routes ?? []).map((r: RouteOption) => {
              const meta = ROUTE_META[r.route] ?? { label: cap(r.route) + " Route", blurb: "", icon: Award, accent: "indigo" };
              const ac = ACCENT[meta.accent] ?? ACCENT.indigo;
              const Icon = meta.icon;
              const selected = selectedRoute === r.route;
              return (
                <button
                  key={r.route}
                  onClick={() => {
                    setSelectedRoute(r.route);
                    setServerBlockers(null);
                  }}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    selected ? `ring-2 ${ac.ring}` : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center ${ac.bg}`}>
                      <Icon className={`w-5 h-5 ${ac.text}`} />
                    </div>
                    {selected ? <CheckCircle2 className={`w-5 h-5 ${ac.text}`} /> : <Circle className="w-5 h-5 text-gray-300" />}
                  </div>
                  <p className="font-semibold text-gray-900">{meta.label}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{meta.blurb}</p>
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Fee</span>
                      <span className="font-bold text-gray-900">{naira(r.fee)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Min CPD</span>
                      <span className="font-medium text-gray-700 inline-flex items-center gap-1.5">
                        {r.cpd_required ?? "—"}
                        {r.cpd_required != null &&
                          (r.cpd_met ? (
                            <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold">MET</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">NOT MET</span>
                          ))}
                      </span>
                    </div>
                    {r.exam_required && (
                      <div className="flex items-center justify-between text-sm gap-2">
                        <span className="text-gray-500 shrink-0">
                          {r.route === "matured" ? "MRI result" : "PCI result"}
                        </span>
                        {r.exam_result_met ? (
                          <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold">MET</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">NOT MET</span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Blockers, then Pay.
          NOT either/or. A declarable bar has to stay on screen after it is
          answered — the tick is the declaration, and it must remain visible
          and reversible — while the payment panel appears below it. Rendering
          one or the other is what left a fellowship candidate with the
          declaration ticked and no button anywhere on the page. */}
      {blockers.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-800">
            {blockersCleared
              ? "Declared — you can now proceed to payment:"
              : "You can’t proceed to payment yet:"}
          </p>
          {blockers.map((b, i) => (
            <BlockerCard
              key={`${b.code}-${i}`}
              blocker={b}
              onClaimed={refetch}
              attested={cpdAttested}
              onAttest={setCpdAttested}
            />
          ))}
          {onBack && !blockersCleared && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" /> Back to my details
            </button>
          )}
        </div>
      )}

      {blockersCleared && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-600">
              {alreadyPaid ? "Upgrade fee (already paid)" : "Upgrade fee"}
              {e.requires_route && selectedRoute && (
                <span className="text-gray-400"> · {cap(selectedRoute)} route</span>
              )}
            </span>
            <span className="text-xl font-bold text-indigo-700">{naira(feeToPay)}</span>
          </div>
          {payError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
              {payError}
            </div>
          )}
          <div className="flex items-center gap-2">
            {alreadyPaid ? (
              <button
                onClick={handleResubmit}
                disabled={!e.form_complete || isResubmitting}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-green-600 text-white text-sm font-semibold hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-600/20 transition-all"
              >
                {isResubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Resubmitting…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Resubmit for review
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handlePay}
                disabled={!canPay || isPaying}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold hover:from-indigo-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-600/20 transition-all"
              >
                {isPaying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" /> Pay {naira(feeToPay)}
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 px-3 py-3 rounded-lg border bg-white text-gray-600 text-sm hover:bg-gray-50"
              title="Refresh eligibility"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
          {disabledReason && !isPaying && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {disabledReason}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-3">
            {alreadyPaid
              ? "You already paid for this application. Resubmitting sends your corrected application straight back for review — no repeat payment."
              : "You’ll be redirected to the secure OnePay gateway. After payment your upgrade application is created and sent for review."}
          </p>
        </div>
      )}
    </div>
  );
}
